<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { userModule } from '@/store/user/module'
import { isSupportWindow } from '@/utils'

type TCreateAccountPayload = {
    firstName: string
    lastName: string
    email: string
    password: string
    signInWithPassword: boolean
}

const {
    Icon,
    Button: PocketButton,
    Input: PocketInput,
    Link: PocketLink,
    Checkbox,
} = UIKit

const props = withDefaults(
    defineProps<{
        isLoading?: boolean
        errorMessage?: string
    }>(),
    { isLoading: false, errorMessage: '' }
)

const emit = defineEmits<{
    submitCreateAccount: [payload: TCreateAccountPayload]
}>()

const router = useRouter()

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const password = ref('')
const signInWithPassword = ref(false)
const termsOfUseChecked = ref(false)

const user = computed(() => {
    return userModule.state.user
})

const isCreateAccountFormValid = computed(() => {
    return firstName.value
        && lastName.value
        && email.value
        && (!signInWithPassword.value || (signInWithPassword.value && password.value.length >= 6))
        && termsOfUseChecked.value
})

const usePassword = () => {
    signInWithPassword.value = true
}

const useMagicLink = () => {
    signInWithPassword.value = false
    password.value = ''
}

const showIntercom = () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('show')
    }
}

const submitCreateAccount = () => {
    const payload: TCreateAccountPayload = {
        firstName: firstName.value,
        lastName: lastName.value,
        email: email.value,
        password: password.value,
        signInWithPassword: signInWithPassword.value,
    }

    emit('submitCreateAccount', payload)

    return payload
}

onMounted(() => {
    if (user.value) {
        firstName.value = user.value.firstName || ''
        lastName.value = user.value.lastName || ''
        email.value = user.value.username || ''
    }
})
</script>

<template>
    <div class="create-account">
        <img
            class="create-account__blobs-left"
            src="@/assets/images/create-blobs-left.svg"
        >
        <img
            class="create-account__blob-right"
            src="@/assets/images/create-blob-right.svg"
        >
        <div class="create-account__info">
            <img
                class="create-account__email-arrow"
                src="@/assets/images/create-account-email-arrow.svg"
            >
            <div class="create-account__info-data-security">
                <img
                    class="create-account__lock-image"
                    src="@/assets/images/lock.svg"
                >
                <span>Your data is safe with us</span>
            </div>
            <div class="create-account__info-sharing">
                We use email to create your account and to invite learners and colleagues to join you.
            </div>
        </div>
        <div class="create-account__text-small">
            Step 1 of 3
        </div>
        <div class="create-account__text-big">
            Create Your Account
        </div>
        <PocketInput
            v-model="firstName"
            class="create-account__first-name"
            field-type="text"
            label="First Name"
        />
        <PocketInput
            v-model="lastName"
            class="create-account__last-name"
            field-type="text"
            label="Last Name"
        />
        <PocketInput
            v-model="email"
            class="create-account__email"
            field-type="email"
            label="Email"
            :disabled="!!user"
        />
        <PocketInput
            v-if="signInWithPassword"
            v-model="password"
            class="create-account__password"
            field-type="password"
            label="Create Password (6+ characters)"
        />
        <div v-if="props.errorMessage" class="create-account__error-message">
            <Icon class="create-account__error-message-icon" type="warning" />
            {{ props.errorMessage }}
        </div>
        <template v-if="!user">
            <div v-if="!signInWithPassword" class="create-account__use-password">
                <PocketButton
                    class="create-account__use-password-btn"
                    type="tertiary"
                    @click="usePassword"
                >
                    Create a password
                </PocketButton>
                instead of using a link to sign in
            </div>
            <div v-else class="create-account__use-magic-link">
                <PocketButton
                    class="create-account__use-magic-link-btn"
                    type="tertiary"
                    @click="useMagicLink"
                >
                    Sign in with a link
                </PocketButton>
                instead of creating a password
            </div>
        </template>
        <div class="create-account__agreements">
            <Checkbox
                v-model="termsOfUseChecked"
                class="create-account__agreements-checkbox"
            />
            <span>
                I agree to the
                <PocketLink
                    class="create-account__terms-of-use-link"
                    href="http://www.pocketprep.com/terms-of-service/"
                    target="_blank"
                >
                    terms of use
                </PocketLink>
                and
                <PocketLink
                    class="create-account__privacy-policy-link"
                    href="http://www.pocketprep.com/privacy-policy/"
                    target="_blank"
                >
                    privacy policy
                </PocketLink>
            </span>
        </div>
        <PocketButton
            class="create-account__submit"
            :disabled="!isCreateAccountFormValid || props.isLoading"
            @click="submitCreateAccount"
        >
            Name Your Organization <Icon class="create-account__submit-arrow" type="arrow" />
        </PocketButton>
        <div class="create-account__footer">
            <span v-if="!user" class="create-account__footer-text">Already have an account?</span>
            <PocketLink
                v-if="!user"
                class="create-account__sign-in-link"
                @click.prevent="router.push({ name: 'sign-in' })"
            >
                Sign In
            </PocketLink>
            <span v-if="!user">•</span>
            <PocketButton
                class="create-account__help-link"
                type="tertiary"
                @click="showIntercom"
                @keydown.enter="showIntercom"
            >
                I need help
            </PocketButton>
        </div>
    </div>
</template>

<style lang="scss" scoped>


.create-account {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 26px;
    padding-bottom: 17px;

    &__blobs-left {
        position: absolute;
        left: 0;
        bottom: 0;
    }

    &__blob-right {
        position: absolute;
        right: 0;
        top: 0;
    }

    &__info {
        position: absolute;
        right: 49px;
        top: 303px;
        width: 200px;
    }

    &__email-arrow {
        position: absolute;
        width: 79px;
        height: 24px;
        top: -30px;
        left: -79px;
    }

    &__info-data-security {
        font-size: 14px;
        line-height: 19px;
        font-weight: 600;
        margin-bottom: 10px;
    }

    &__lock-image {
        width: 18px;
        height: 18px;
        margin-right: 8px;
        margin-left: -3px;
        margin-bottom: -2px;
    }

    &__info-sharing {
        font-size: 14px;
        line-height: 19.12px;
    }

    &__text-small {
        font-size: 13px;
        line-height: 17.76px;
        color: $slate-03;
        margin-bottom: 10px;
    }

    &__text-big {
        font-size: 20px;
        font-weight: 600;
        line-height: 27px;
        margin-bottom: 44px;
    }

    &__first-name,
    &__last-name {
        width: 320px;
        margin-bottom: 19px;
    }

    &__email {
        width: 320px;
        margin-bottom: 16px;
    }

    &__password {
        width: 320px;
        margin-top: 3px;
        margin-bottom: 11px;
    }

    &__error-message {
        display: flex;
        align-items: center;
        color: #c238f3;
        font-size: 14px;
        line-height: 19px;
        margin-bottom: 18px;
    }

    &__error-message-icon {
        margin-right: 10px;
    }

    &__use-password {
        font-size: 13px;
        margin-bottom: 32px;
    }

    &__use-magic-link {
        font-size: 13px;
        margin-bottom: 22px;
    }

    &__use-password-btn,
    &__use-magic-link-btn {
        font-size: 13px !important;
    }

    &__agreements {
        display: flex;
        align-items: center;
        margin-bottom: 22px;
        font-size: 13px;
        color: $slate-01;
    }

    &__agreements-checkbox {
        margin-right: 10px;
        margin-bottom: 2px;
    }

    &__terms-of-use-link,
    &__privacy-policy-link {
        font-size: 13px !important;
    }

    &__submit {
        margin: 0 16px 54px;
        padding: 9px 14px 8px 14px !important;
    }

    &__submit-arrow {
        margin-left: 5px;
    }

    &__footer {
        color: $brand-blue;
        font-size: 14px;
        font-weight: 400;
    }

    &__footer-text {
        color: $brand-black;
    }

    &__sign-in-link,
    &__help-link {
        margin: 0 10px;
    }
}
</style>
